import { hash01, mulberry32 } from './hash';

/**
 * 4×4 variants.
 * R = spall coverage (dark clay patches — reserved for spalled/rim near the hole)
 * G = corner chips (rim only)
 * B = HAIRLINE cracks (thin SDF lines; cracked-state uses this channel only)
 * A = pock crater
 */
export const ATLAS_SIZE = 512;
export const ATLAS_COLS = 4;
export const ATLAS_TILE = ATLAS_SIZE / ATLAS_COLS;
export const ATLAS_VARIANTS = ATLAS_COLS * ATLAS_COLS;

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

function valueNoise(x: number, y: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const n00 = hash01(ix, iy, seed);
  const n10 = hash01(ix + 1, iy, seed);
  const n01 = hash01(ix, iy + 1, seed);
  const n11 = hash01(ix + 1, iy + 1, seed);
  return n00 * (1 - ux) * (1 - uy) + n10 * ux * (1 - uy) + n01 * (1 - ux) * uy + n11 * ux * uy;
}

function fbm(x: number, y: number, seed: number): number {
  return (
    valueNoise(x, y, seed) * 0.5
    + valueNoise(x * 2.1, y * 2.1, seed + 3) * 0.3
    + valueNoise(x * 4.3, y * 4.3, seed + 7) * 0.2
  );
}

function distToSeg(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const l2 = dx * dx + dy * dy;
  if (l2 < 1e-10) return Math.hypot(px - x0, py - y0);
  let t = ((px - x0) * dx + (py - y0) * dy) / l2;
  t = Math.min(1, Math.max(0, t));
  return Math.hypot(px - (x0 + t * dx), py - (y0 + t * dy));
}

interface TileSpec {
  seed: number;
  spallCx: number;
  spallCy: number;
  spallRx: number;
  spallRy: number;
  corners: number;
  crackStyle: 'stair' | 'hair' | 'slash' | 'fork';
}

const TILES: TileSpec[] = [
  { seed: 17, spallCx: 0.42, spallCy: 0.55, spallRx: 0.36, spallRy: 0.3, corners: 0b1001, crackStyle: 'stair' },
  { seed: 91, spallCx: 0.62, spallCy: 0.38, spallRx: 0.32, spallRy: 0.38, corners: 0b0110, crackStyle: 'hair' },
  { seed: 233, spallCx: 0.5, spallCy: 0.48, spallRx: 0.26, spallRy: 0.24, corners: 0b1100, crackStyle: 'slash' },
  { seed: 419, spallCx: 0.34, spallCy: 0.62, spallRx: 0.4, spallRy: 0.28, corners: 0b0011, crackStyle: 'fork' },
  { seed: 1009, spallCx: 0.28, spallCy: 0.4, spallRx: 0.3, spallRy: 0.34, corners: 0b1010, crackStyle: 'stair' },
  { seed: 2027, spallCx: 0.7, spallCy: 0.58, spallRx: 0.28, spallRy: 0.26, corners: 0b0101, crackStyle: 'hair' },
  { seed: 3331, spallCx: 0.48, spallCy: 0.32, spallRx: 0.34, spallRy: 0.22, corners: 0b1110, crackStyle: 'slash' },
  { seed: 4093, spallCx: 0.56, spallCy: 0.7, spallRx: 0.24, spallRy: 0.3, corners: 0b0111, crackStyle: 'fork' },
  { seed: 5039, spallCx: 0.38, spallCy: 0.36, spallRx: 0.22, spallRy: 0.28, corners: 0b1000, crackStyle: 'hair' },
  { seed: 7901, spallCx: 0.66, spallCy: 0.46, spallRx: 0.38, spallRy: 0.2, corners: 0b0010, crackStyle: 'stair' },
  { seed: 9743, spallCx: 0.52, spallCy: 0.6, spallRx: 0.2, spallRy: 0.22, corners: 0b0100, crackStyle: 'slash' },
  { seed: 11251, spallCx: 0.3, spallCy: 0.52, spallRx: 0.26, spallRy: 0.36, corners: 0b0001, crackStyle: 'fork' },
  { seed: 13007, spallCx: 0.58, spallCy: 0.28, spallRx: 0.32, spallRy: 0.24, corners: 0b1101, crackStyle: 'stair' },
  { seed: 15401, spallCx: 0.44, spallCy: 0.72, spallRx: 0.28, spallRy: 0.2, corners: 0b1011, crackStyle: 'hair' },
  { seed: 17863, spallCx: 0.72, spallCy: 0.34, spallRx: 0.22, spallRy: 0.32, corners: 0b0110, crackStyle: 'slash' },
  { seed: 19913, spallCx: 0.4, spallCy: 0.44, spallRx: 0.3, spallRy: 0.3, corners: 0b1111, crackStyle: 'fork' },
];

interface CrackSeg {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  w: number;
}

function buildCracks(rng: () => number, style: TileSpec['crackStyle']): CrackSeg[] {
  const cracks: CrackSeg[] = [];
  const push = (x0: number, y0: number, x1: number, y1: number, w: number) => {
    cracks.push({ x0, y0, x1, y1, w });
  };
  // Hairline widths stay well under a tile percent so mipmaps collapse to
  // a faint vein instead of a dark blob. Shader fwidth-fades them at distance.
  const hairW = () => 0.0042 + rng() * 0.0024;
  if (style === 'stair') {
    let x = 0.12 + rng() * 0.2;
    let y = 0.18 + rng() * 0.2;
    const steps = 3 + Math.floor(rng() * 2);
    for (let i = 0; i < steps; i += 1) {
      const horiz = i % 2 === 0;
      const len = 0.16 + rng() * 0.24;
      const nx = horiz ? Math.min(0.88, x + len) : x;
      const ny = horiz ? y : Math.min(0.88, y + len);
      push(x, y, nx, ny, hairW());
      x = nx;
      y = ny;
    }
  } else if (style === 'hair') {
    const n = 3 + Math.floor(rng() * 2);
    for (let i = 0; i < n; i += 1) {
      const x0 = 0.12 + rng() * 0.72;
      const y0 = 0.12 + rng() * 0.72;
      const ang = rng() * Math.PI;
      const len = 0.16 + rng() * 0.28;
      push(x0, y0, x0 + Math.cos(ang) * len, y0 + Math.sin(ang) * len, hairW());
    }
  } else if (style === 'slash') {
    const x0 = 0.12 + rng() * 0.18;
    const y0 = 0.16 + rng() * 0.28;
    const x1 = 0.68 + rng() * 0.2;
    const y1 = 0.58 + rng() * 0.26;
    push(x0, y0, x1, y1, hairW());
    if (rng() > 0.35) {
      const mx = (x0 + x1) * 0.5;
      const my = (y0 + y1) * 0.5;
      push(mx, my, mx + 0.08 + rng() * 0.12, my - 0.04 - rng() * 0.1, hairW());
    }
  } else {
    const cx = 0.4 + rng() * 0.2;
    const cy = 0.4 + rng() * 0.2;
    const arms = 3;
    for (let i = 0; i < arms; i += 1) {
      const ang = (i / arms) * Math.PI + rng() * 0.4;
      const len = 0.18 + rng() * 0.24;
      push(cx, cy, cx + Math.cos(ang) * len, cy + Math.sin(ang) * len, hairW());
    }
  }
  return cracks;
}

function bakeTile(data: Uint8Array, col: number, row: number, spec: TileSpec, size: number, tile: number): void {
  const rng = mulberry32(spec.seed);
  const cracks = buildCracks(rng, spec.crackStyle);
  const bites: Array<{ cx: number; cy: number; r: number; add: boolean }> = [];
  const nBites = 4 + Math.floor(rng() * 4);
  for (let i = 0; i < nBites; i += 1) {
    const ang = rng() * Math.PI * 2;
    bites.push({
      cx: spec.spallCx + Math.cos(ang) * spec.spallRx * (0.7 + rng() * 0.45),
      cy: spec.spallCy + Math.sin(ang) * spec.spallRy * (0.7 + rng() * 0.45),
      r: 0.04 + rng() * 0.08,
      add: rng() > 0.4,
    });
  }
  const ox = col * tile;
  const oy = row * tile;
  for (let ty = 0; ty < tile; ty += 1) {
    for (let tx = 0; tx < tile; tx += 1) {
      const u = (tx + 0.5) / tile;
      const v = (ty + 0.5) / tile;
      const n = fbm(u * 5.5, v * 5.5, spec.seed);
      const grain = fbm(u * 14, v * 12, spec.seed + 11);
      const dx = (u - spec.spallCx) / spec.spallRx;
      const dy = (v - spec.spallCy) / spec.spallRy;
      let ell = Math.hypot(dx, dy) + (n - 0.5) * 0.72;
      for (const bite of bites) {
        const bd = Math.hypot(u - bite.cx, v - bite.cy) / bite.r;
        if (bite.add) ell -= Math.max(0, 1 - bd) * 0.28;
        else ell += Math.max(0, 1 - bd) * 0.22;
      }
      const spall = smoothstep(1.08, 0.48, ell);

      const edge = Math.min(u, 1 - u, v, 1 - v);
      let corner = 0;
      const cr = 0.2;
      if (spec.corners & 1) corner = Math.max(corner, smoothstep(cr, 0.02, Math.hypot(u, v)));
      if (spec.corners & 2) corner = Math.max(corner, smoothstep(cr, 0.02, Math.hypot(1 - u, v)));
      if (spec.corners & 4) corner = Math.max(corner, smoothstep(cr, 0.02, Math.hypot(u, 1 - v)));
      if (spec.corners & 8) corner = Math.max(corner, smoothstep(cr, 0.02, Math.hypot(1 - u, 1 - v)));
      corner *= 0.7 + n * 0.3;

      let crack = 0;
      for (const s of cracks) {
        const d = distToSeg(u, v, s.x0, s.y0, s.x1, s.y1);
        // Tight SDF: no fbm fattening (that was the phase-2 blob speckle).
        crack = Math.max(crack, smoothstep(s.w * 1.35, s.w * 0.22, d));
      }

      const pockR = Math.hypot(u - 0.5, v - 0.5) + (n - 0.5) * 0.16;
      const pock = smoothstep(0.15, 0.04, pockR) * (0.7 + edge * 0.3);

      const i = ((oy + ty) * size + (ox + tx)) * 4;
      const r = Math.min(1, Math.max(0, spall * (0.88 + grain * 0.2)));
      data[i] = Math.round(r * 255);
      data[i + 1] = Math.round(corner * 255);
      data[i + 2] = Math.round(crack * 255);
      data[i + 3] = Math.round(pock * 255);
    }
  }
}

export function bakeSpallAtlas(size = ATLAS_SIZE): {
  data: Uint8Array;
  width: number;
  height: number;
  tile: number;
} {
  const tile = size / ATLAS_COLS;
  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < ATLAS_VARIANTS; i += 1) {
    const col = i % ATLAS_COLS;
    const row = Math.floor(i / ATLAS_COLS);
    bakeTile(data, col, row, TILES[i], size, tile);
  }
  return { data, width: size, height: size, tile };
}

export function variantOffset(variant: number): { u: number; v: number } {
  const v = ((variant % ATLAS_VARIANTS) + ATLAS_VARIANTS) % ATLAS_VARIANTS;
  return { u: (v % ATLAS_COLS) / ATLAS_COLS, v: Math.floor(v / ATLAS_COLS) / ATLAS_COLS };
}
